import { CircleCheckBigIcon, EmptyState } from '@buffer-mono/popcorn'
import { useReducedMotion } from 'motion/react'
import * as m from 'motion/react-m'

import {
  EmptyStateAlignedWithFeed,
  EmptyStateWrapper,
} from '../../components/EmptyStates'
import { MentionsStatsBar } from './MentionsStatsBar'
import type { MentionsEmptyStateChannel } from './types'

import styles from './MentionsCaughtUpEmptyState.module.css'

import type { JSX } from 'react'

const EmptyStateIconMotion = m.create(EmptyState.Icon)

type MentionsCaughtUpEmptyStateProps = {
  /**
   * The user emptied the list themselves in this session. Switches to the
   * celebratory heading and plays the pulse, which would read as noise on an
   * inbox that was already empty when the page loaded.
   */
  justResolvedLastMention?: boolean
  /** Channels the recent-mention count covers. An empty list drops the bar. */
  channels?: MentionsEmptyStateChannel[]
  /**
   * Whether mention cards follow this state. The recent-mention bar is a
   * header for those cards, so on a genuinely empty inbox it would sit alone
   * over blank space, reporting a count of nothing.
   */
  hasMentionsBelow?: boolean
}

/**
 * Shown when the channels in scope have had mentions before but none are
 * waiting now. Distinct from the listening state: this account has proof the
 * pipeline works, so it gets no explanation about missing history.
 */
export const MentionsCaughtUpEmptyState = ({
  justResolvedLastMention = false,
  channels = [],
  hasMentionsBelow = false,
}: MentionsCaughtUpEmptyStateProps = {}): JSX.Element => {
  const prefersReducedMotion = useReducedMotion()
  const shouldAnimate = justResolvedLastMention && !prefersReducedMotion

  return (
    <EmptyStateWrapper shouldAnimate={shouldAnimate}>
      <EmptyStateAlignedWithFeed>
        <div>
          <div className={styles.layoutWrapper}>
            <EmptyState
              size="large"
              variant="success"
              // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- EmptyState is the labelled live status while retaining its Popcorn presentation.
              role="status"
              aria-label="All mentions have been addressed"
            >
              <div className={styles.animationContainer} aria-hidden="true">
                <EmptyStateIconMotion
                  className={styles.emptyStateIcon}
                  animate={
                    shouldAnimate
                      ? {
                          scale: [1, 1.2, 1],
                          transition: { duration: 1 },
                          backgroundColor: [
                            'var(--color-bg-success-subtle)',
                            'var(--global-color-green-300)',
                            'var(--color-bg-success-subtle)',
                          ],
                        }
                      : undefined
                  }
                />
                <CircleCheckBigIcon
                  className={styles.checkCircleIcon}
                  color="success"
                  size="small"
                />
              </div>
              <EmptyState.Heading>
                {justResolvedLastMention
                  ? 'Congratulations! This was the last mention 👏'
                  : "You're all caught up! 🎉"}
              </EmptyState.Heading>
              <EmptyState.Description>
                We&apos;ll let you know when new mentions appear.
              </EmptyState.Description>
            </EmptyState>
          </div>
          {hasMentionsBelow && channels.length > 0 && (
            <MentionsStatsBar channels={channels} />
          )}
        </div>
      </EmptyStateAlignedWithFeed>
    </EmptyStateWrapper>
  )
}
